import React, { useState } from "react";
import { Clock, Globe, Monitor, ArrowRight, Sparkles } from "lucide-react";
import { PERSONAS, CATEGORIES } from "../demoData";
import DemoBanner from "./DemoBanner";

// Deterministic on-brand avatar tint from the persona name (no external images).
const AVATAR_TINTS = [
  "bg-emerald-100 text-emerald-800",
  "bg-teal-100 text-teal-800",
  "bg-green-100 text-green-800",
  "bg-cyan-100 text-cyan-800",
  "bg-lime-100 text-lime-800",
];
const tintFor = (s) => AVATAR_TINTS[[...s].reduce((n, c) => n + c.charCodeAt(0), 0) % AVATAR_TINTS.length];
const initials = (name, role) =>
  (name?.[0] || "") + (role?.split(" ").slice(-1)[0]?.[0] || "");

export default function InterviewGallery({ onPick, onTalkToZia }) {
  const [cat, setCat] = useState("All");
  const list = cat === "All" ? PERSONAS : PERSONAS.filter((p) => p.category === cat);

  return (
    <div className="py-6">
      {/* Riya hero — live AI hiring-consultant demo (shared DemoBanner component) */}
      {onTalkToZia && (
        <div className="mb-12">
          <DemoBanner onStart={onTalkToZia} />
        </div>
      )}

      {/* Heading */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 text-primary text-sm font-semibold mb-3">
          <Sparkles size={16} /> LIVE INTERACTIVE DEMO
        </div>
        <h1 className="text-3xl md:text-4xl font-bold text-foreground">Try our AI interviews</h1>
        <p className="text-muted-foreground mt-3 max-w-2xl mx-auto">
          Pick an interviewer. Start talking. Get a genuine AI evaluation of your answers.
        </p>
      </div>

      {/* Category filter */}
      <div className="flex flex-wrap justify-center gap-2 mb-8">
        {CATEGORIES.map((c) => (
          <button
            key={c}
            onClick={() => setCat(c)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium border transition ${
              cat === c
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-card text-foreground border-border hover:bg-accent"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Card grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {list.map((p) => (
          <PersonaCard key={p.id} persona={p} onPick={onPick} />
        ))}
      </div>
    </div>
  );
}

function PersonaCard({ persona, onPick }) {
  const extra = persona.tags.length > 2 ? persona.tags.length - 2 : 0;
  return (
    <div className="relative rounded-2xl border border-border bg-card overflow-hidden shadow-sm hover:shadow-lg hover:border-primary/40 transition group">
      {/* Category ribbon */}
      <div className="absolute -left-9 top-4 -rotate-45 bg-foreground text-background text-[10px] font-bold tracking-wide px-10 py-1 shadow">
        {persona.category}
      </div>
      {/* NEW badge */}
      {persona.isNew && (
        <span className="absolute right-3 top-3 rounded-full bg-primary text-primary-foreground text-[10px] font-bold px-2 py-0.5">
          NEW
        </span>
      )}

      <div className="p-6 flex flex-col items-center text-center">
        <div className={`w-20 h-20 rounded-full flex items-center justify-center text-xl font-bold ${tintFor(persona.name)} ring-2 ring-border`}>
          {initials(persona.name, persona.role).toUpperCase()}
        </div>
        <div className="mt-3 font-bold text-foreground">{persona.name}</div>
        <div className="text-sm text-primary font-medium">{persona.role}</div>

        {/* Tags */}
        <div className="flex flex-wrap justify-center gap-1.5 mt-3">
          {persona.tags.slice(0, 2).map((t) => (
            <span key={t} className="rounded-full border border-border px-2.5 py-0.5 text-xs text-muted-foreground">{t}</span>
          ))}
          {extra > 0 && <span className="text-xs text-muted-foreground self-center">+{extra} more</span>}
        </div>

        {/* Meta */}
        <div className="flex items-center justify-center flex-wrap gap-x-4 gap-y-1 mt-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><Clock size={13} /> 3 min</span>
          <span className="flex items-center gap-1"><Globe size={13} /> English</span>
          <span className="flex items-center gap-1"><Monitor size={13} /> Desktop</span>
        </div>

        {/* CTA */}
        <button
          onClick={() => onPick(persona)}
          className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold shadow hover:bg-primary/90 transition"
        >
          Try Interview <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform" />
        </button>
      </div>
    </div>
  );
}
